import { createContext, useContext, useState, useRef, useEffect, useCallback, useMemo, type ReactNode } from 'react'
import { supabase } from '../lib/supabase'

const MeetingCtx = createContext(null)

export function MeetingProvider({ children }: { children: ReactNode }) {
  const [meeting,        setMeeting]        = useState(null)  // { reuniao, projetoId }
  const [mode,           setMode]           = useState('idle')
  const [displayMode,    setDisplayMode]    = useState('fullscreen') // fullscreen | compact | capsule
  const [seconds,        setSeconds]        = useState(0)
  const [paused,         setPaused]         = useState(false)
  const [didSetIniciado, setDidSetIniciado] = useState(false)
  const timerRef     = useRef(null)
  const pausedRef    = useRef(false)  // lido dentro do interval sem closure stale
  const iniciadoRef  = useRef(null)   // guarda iniciado_em para recálculo em visibilitychange

  function openWelcome(reuniao, projetoId) {
    setMeeting({ reuniao, projetoId })
    setMode('welcome')
    setDisplayMode('fullscreen')
  }

  async function startFromWelcome() {
    const ini = meeting?.reuniao?.iniciado_em
    const elapsed = ini
      ? Math.max(0, Math.round((Date.now() - new Date(ini).getTime()) / 1000))
      : 0
    _startTimer(elapsed, ini)
    setMode('live')
    setDisplayMode('fullscreen')
    if (meeting?.reuniao?.id && !ini) {
      const ts = new Date().toISOString()
      iniciadoRef.current = ts
      const { error } = await supabase.from('reunioes').update({ iniciado_em: ts }).eq('id', meeting.reuniao.id)
      if (error) console.error('Falha ao salvar iniciado_em:', error)
      setMeeting(prev => ({ ...prev, reuniao: { ...prev.reuniao, iniciado_em: ts } }))
      setDidSetIniciado(true)
    } else { setDidSetIniciado(false) }
  }

  async function startMeeting(reuniao, projetoId) {
    const ts = new Date().toISOString()
    const ini = reuniao.iniciado_em || ts
    const reuniaoAtualizada = !reuniao.iniciado_em ? { ...reuniao, iniciado_em: ts } : reuniao
    setMeeting({ reuniao: reuniaoAtualizada, projetoId })
    const elapsed = reuniao.iniciado_em
      ? Math.max(0, Math.round((Date.now() - new Date(reuniao.iniciado_em).getTime()) / 1000))
      : 0
    _startTimer(elapsed, ini)
    setMode('live')
    setDisplayMode('fullscreen')
    if (!reuniao.iniciado_em) {
      const { error } = await supabase.from('reunioes').update({ iniciado_em: ts }).eq('id', reuniao.id)
      if (error) console.error('Falha ao salvar iniciado_em:', error)
      setDidSetIniciado(true)
    } else { setDidSetIniciado(false) }
  }

  function _startTimer(offsetSeconds = 0, iniciadoEm = null) {
    clearInterval(timerRef.current)
    pausedRef.current = false
    iniciadoRef.current = iniciadoEm
    setPaused(false)
    setSeconds(offsetSeconds)
    timerRef.current = setInterval(() => {
      if (!pausedRef.current) setSeconds(s => s + 1)
    }, 1000)
  }

  // Recalcular timer quando a página volta do background (browser throttle corrige drift)
  useEffect(() => {
    function onVisibility() {
      if (document.visibilityState !== 'visible') return
      if (!iniciadoRef.current || pausedRef.current) return
      const real = Math.max(0, Math.round((Date.now() - new Date(iniciadoRef.current).getTime()) / 1000))
      setSeconds(real)
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [])

  function pauseMeeting() {
    pausedRef.current = true
    setPaused(true)
  }

  function resumeMeeting() {
    pausedRef.current = false
    setPaused(false)
  }

  async function endMeeting() {
    clearInterval(timerRef.current)
    pausedRef.current = false
    iniciadoRef.current = null
    setMeeting(null); setMode('idle'); setDisplayMode('fullscreen')
    setSeconds(0); setPaused(false); setDidSetIniciado(false)
  }

  async function cancelMeeting() {
    clearInterval(timerRef.current)
    pausedRef.current = false
    iniciadoRef.current = null
    if (didSetIniciado && meeting?.reuniao?.id)
      await supabase.from('reunioes').update({ iniciado_em: null }).eq('id', meeting.reuniao.id)
    setMeeting(null); setMode('idle'); setDisplayMode('fullscreen')
    setSeconds(0); setPaused(false); setDidSetIniciado(false)
  }

  // Entrar numa reunião já ativa em outro device (sem setar iniciado_em)
  function joinMeeting(reuniao, projetoId) {
    setMeeting({ reuniao, projetoId })
    const elapsed = reuniao.iniciado_em
      ? Math.max(0, Math.round((Date.now() - new Date(reuniao.iniciado_em).getTime()) / 1000))
      : 0
    _startTimer(elapsed, reuniao.iniciado_em)
    setMode('live')
    setDisplayMode('fullscreen')
    setDidSetIniciado(false)
  }

  async function updateMeeting(fields) {
    const id = meeting?.reuniao?.id
    if (!id) { console.warn('updateMeeting: sem meeting.reuniao.id, ignorando'); return }
    const { error } = await supabase.from('reunioes').update(fields).eq('id', id)
    if (error) throw error
    setMeeting(prev => prev ? { ...prev, reuniao: { ...prev.reuniao, ...fields } } : null)
  }

  // Realtime: detectar quando a reunião é encerrada/cancelada em outro dispositivo
  useEffect(() => {
    const id = meeting?.reuniao?.id
    if (!id || mode !== 'live') return

    const channel = supabase
      .channel(`meeting-sync-${id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'reunioes',
        filter: `id=eq.${id}`,
      }, (payload) => {
        const row = payload.new
        // Reunião encerrada ou cancelada externamente
        if (row.status !== 'AGENDADA' || !row.iniciado_em) {
          clearInterval(timerRef.current)
          pausedRef.current = false
          iniciadoRef.current = null
          setMeeting(null); setMode('idle'); setDisplayMode('fullscreen')
          setSeconds(0); setPaused(false); setDidSetIniciado(false)
        }
      })
      .subscribe()

    return () => { supabase.removeChannel(channel) }
  }, [meeting?.reuniao?.id, mode])

  useEffect(() => () => clearInterval(timerRef.current), [])

  const toFullscreen = useCallback(() => setDisplayMode('fullscreen'), [])
  const toCompact    = useCallback(() => setDisplayMode('compact'), [])
  const toCapsule    = useCallback(() => setDisplayMode('capsule'), [])

  const value = useMemo(() => ({
    meeting, mode, displayMode, seconds, paused,
    openWelcome, startMeeting, startFromWelcome, joinMeeting,
    endMeeting, cancelMeeting, updateMeeting,
    pauseMeeting, resumeMeeting,
    toFullscreen, toCompact, toCapsule,
    // Compat aliases
    minimized: displayMode === 'capsule',
    minimize: toCapsule,
    expand: toFullscreen,
  }), [meeting, mode, displayMode, seconds, paused,
    openWelcome, startMeeting, startFromWelcome, joinMeeting,
    endMeeting, cancelMeeting, updateMeeting,
    pauseMeeting, resumeMeeting, toFullscreen, toCompact, toCapsule])

  return (
    <MeetingCtx.Provider value={value}>
      {children}
    </MeetingCtx.Provider>
  )
}

export function useMeeting() {
  const ctx = useContext(MeetingCtx)
  if (!ctx) throw new Error('useMeeting fora do MeetingProvider')
  return ctx
}
